/* eslint-disable camelcase */
import { ErrorResponse, ErrorTimeout, User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import * as Sentry from '@sentry/react';

export class SessionExpiredError extends Error {
  constructor() {
    super('Innskráning er útrunnin. Skráðu þig inn aftur og reyndu svo að vista.');
  }
}

export class AuthenticationUnavailableError extends Error {
  constructor() {
    super('Ekki náðist að endurnýja innskráningu. Reyndu aftur eftir smástund.');
  }
}

class AuthService {
  private readonly manager: UserManager;
  private user: User | null = null;
  private renewal: Promise<User> | null = null;
  private loginRequest: Promise<void> | null = null;
  private authenticationRequest?: Promise<User | undefined>;
  private callbackUrl = this.isAuthenticationCallback() ? window.location.href : undefined;
  private readonly callbackRecoveryKey = 'londunarkerfi:callback-recovery';
  private readonly tabSessionKey = 'londunarkerfi:auth-tab';
  private tabInitialization?: Promise<void>;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retryDelay = 5000;
  private retryAfter = 0;
  private sessionExpired = false;
  private signingOut = false;
  private authenticating = this.isAuthenticationCallback();
  private readonly listeners = new Set<() => void>();

  constructor() {
    // Hreinsum slóðina áður en React og Sentry hefja skráningu á síðunni.
    if (this.callbackUrl) window.history.replaceState(window.history.state, '', window.location.pathname);
    this.manager = new UserManager({
      authority: process.env.REACT_APP_AUTHORITY || '',
      client_id: '@fiskistofa.is/aflaskraning',
      redirect_uri: process.env.REACT_APP_REDIRECT_URI || '',
      response_type: 'code',
      loadUserInfo: false,
      // Allar endurnýjanir fara í gegnum sömu biðröð, líka sjálfvirkar endurnýjanir.
      automaticSilentRenew: false,
      requestTimeoutInSeconds: 30,
      silentRequestTimeoutInSeconds: 15,
      post_logout_redirect_uri: 'https://island.is/s/fiskistofa',
      scope: 'openid profile offline_access @fiskistofa.is/aflaskraning',
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      // Beiðnin þarf að vera aðgengileg bæði aðalglugga og innskráningarglugga.
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        token_endpoint: `${process.env.REACT_APP_API_BASE_URL}connect/token`,
        authorization_endpoint: `${process.env.REACT_APP_AUTHORITY}connect/authorize`,
        userinfo_endpoint: `${process.env.REACT_APP_AUTHORITY}connect/userinfo`,
        end_session_endpoint: `${process.env.REACT_APP_AUTHORITY}connect/endsession`,
      },
    });
    this.manager.events.addUserLoaded(user => { this.user = user; });
    this.manager.events.addAccessTokenExpiring(this.renewInBackground);
    this.manager.events.addAccessTokenExpired(this.renewInBackground);
    window.addEventListener('online', this.resumeRenewal);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.resumeRenewal();
    });
    // Endurræsum tímamæla fyrir innskráningu sem var til fyrir endurhleðslu síðunnar.
    if (!this.authenticating) void this.getUser().catch(() => undefined);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  isSessionExpired = (): boolean => this.sessionExpired;

  async rejectAccessToken(accessToken: string): Promise<void> {
    const current = await this.getUser();
    // Seint svar við eldri beiðni má ekki ógilda nýja innskráningu.
    if (current?.access_token !== accessToken) return;
    this.clearRetry();
    this.setSessionExpired(true);
    await this.manager.removeUser();
  }

  private setSessionExpired(expired: boolean) {
    this.sessionExpired = expired;
    this.listeners.forEach(listener => listener());
  }

  async getUser(): Promise<User | null> {
    this.tabInitialization ??= this.initializeTab();
    await this.tabInitialization;
    const user = await this.manager.getUser();
    if (user) this.user = user;
    return user;
  }

  private initializeTab(): Promise<void> {
    if (!navigator.locks) return Promise.resolve();
    const tabId = window.sessionStorage.getItem(this.tabSessionKey) ?? crypto.randomUUID();
    window.sessionStorage.setItem(this.tabSessionKey, tabId);
    return new Promise<void>((resolve, reject) => {
      void navigator.locks.request(`londunarkerfi:auth-tab:${tabId}`, { ifAvailable: true }, async lock => {
        if (!lock) {
          // Afritaður flipi má ekki nota sama einnota refresh-token og upprunalegi flipinn.
          window.sessionStorage.removeItem(this.tabSessionKey);
          await this.manager.removeUser();
        }
        resolve();
        // Lásinn tilheyrir þessari síðu og vafrinn sleppir honum við lokun eða endurhleðslu.
        if (lock) await new Promise<void>(() => undefined);
      }).catch(reject);
    });
  }

  async getValidUser(): Promise<User> {
    if (this.sessionExpired || this.signingOut) throw new SessionExpiredError();
    const user = await this.getUser();
    if (!user) {
      this.setSessionExpired(true);
      throw new SessionExpiredError();
    }
    if (!this.renewal && (user.expires_in ?? 0) > 60) return user;
    try {
      return await this.renewToken();
    } catch (error) {
      // Gilt auðkenni má enn nota meðan tímabundin samskiptavilla gengur yfir.
      if (!this.sessionExpired && !this.signingOut && !user.expired) return user;
      throw error;
    }
  }

  renewToken(): Promise<User> {
    if (this.authenticating) return Promise.reject(this.temporaryError());
    if (this.renewal) return this.renewal;
    if (this.sessionExpired || this.signingOut) return Promise.reject(new SessionExpiredError());
    if (Date.now() < this.retryAfter) return Promise.reject(this.temporaryError());
    this.renewal = this.refreshUser().finally(() => { this.renewal = null; });
    return this.renewal;
  }

  private async refreshUser(): Promise<User> {
    try {
      const previous = await this.getUser();
      if (!previous?.refresh_token) throw new SessionExpiredError();
      const user = await this.manager.signinSilent({
        silentRequestTimeoutInSeconds: this.manager.settings.silentRequestTimeoutInSeconds,
      });
      if (!user) throw new SessionExpiredError();
      if (this.signingOut) {
        await this.manager.removeUser();
        throw new SessionExpiredError();
      }
      this.clearRetry();
      return user;
    } catch (error) {
      const requiresLogin = error instanceof SessionExpiredError ||
        (error instanceof ErrorResponse && ['invalid_grant', 'login_required', 'interaction_required',
          'consent_required', 'account_selection_required'].includes(error.error ?? ''));
      if (requiresLogin) {
        this.clearRetry();
        this.setSessionExpired(true);
        await this.manager.removeUser();
        throw new SessionExpiredError();
      }
      if (this.retryDelay === 5000) {
        this.reportAuthenticationFailure(error, 'refresh');
      }
      this.retryAfter = Date.now() + this.retryDelay;
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(this.renewInBackground, this.retryDelay);
      this.retryDelay = Math.min(this.retryDelay * 2, 60000);
      throw this.temporaryError();
    }
  }

  private temporaryError(): Error {
    return new AuthenticationUnavailableError();
  }

  private reportAuthenticationFailure(error: unknown, operation: 'refresh' | 'callback') {
    let failure = 'unexpected';
    if (error instanceof ErrorTimeout) failure = 'timeout';
    else if (error instanceof TypeError) failure = 'type_error';
    else if (error instanceof ErrorResponse) {
      const knownErrors = ['invalid_grant', 'invalid_client', 'invalid_request', 'access_denied',
        'login_required', 'interaction_required', 'consent_required', 'account_selection_required',
        'server_error', 'temporarily_unavailable'];
      const code = error.error ?? '';
      failure = knownErrors.includes(code) ? code : 'oauth_error';
    } else if (error instanceof Error) {
      if (error.message === 'No state in response') failure = 'missing_response_state';
      else if (error.message === 'No matching state found in storage') failure = 'missing_stored_state';
    }
    // Aðeins fastir villuflokkar og staða vafrans; engin auðkenni eða hrá villusvör.
    Sentry.withScope(scope => {
      if (operation === 'callback') {
        // Slóðir og ferill innskráningar geta innihaldið einnota kóða og state.
        scope.addEventProcessor(event => ({
          ...event,
          request: { url: `${window.location.origin}/signin-oidc` },
          breadcrumbs: [],
        }));
      }
      Sentry.captureMessage(operation === 'refresh'
        ? 'Tímabundin villa við endurnýjun innskráningar'
        : 'Ekki tókst að ljúka innskráningu', {
        level: 'warning',
        tags: {
          auth_operation: operation,
          auth_failure: failure,
          browser_online: String(navigator.onLine),
          page_visibility: document.visibilityState,
        },
      });
    });
  }

  private clearRetry() {
    clearTimeout(this.retryTimer);
    this.retryAfter = 0;
    this.retryDelay = 5000;
  }

  private renewInBackground = () => {
    if (this.authenticating || this.isAuthenticationCallback() || !navigator.onLine) return;
    void this.renewToken().catch(() => undefined);
  };

  private resumeRenewal = () => {
    if (this.sessionExpired || this.signingOut || this.authenticating || this.isAuthenticationCallback() ||
      !navigator.onLine) return;
    this.retryAfter = 0;
    void this.getUser().then(user => {
      if (user && (user.expires_in ?? 0) <= 60) this.renewInBackground();
    }).catch(() => undefined);
  };

  login(): Promise<void> {
    // Handvirk tilraun má hefja nýtt innskráningarferli eftir misheppnaða endurheimt.
    window.sessionStorage.removeItem(this.callbackRecoveryKey);
    return this.startLogin();
  }

  private startLogin(): Promise<void> {
    if (!this.loginRequest) {
      const redirectUri = new URL(this.manager.settings.redirect_uri, window.location.origin);
      if (window.location.origin !== redirectUri.origin) {
        // Beiðnin og svarið þurfa sama origin til að finna sama state í vafranum.
        window.location.replace(new URL('/', redirectUri).href);
        return Promise.resolve();
      }
      this.loginRequest = this.manager.signinRedirect({ redirectMethod: 'replace' })
        .finally(() => { this.loginRequest = null; });
    }
    return this.loginRequest;
  }

  private async recoverMissingState(): Promise<boolean> {
    // Sprettigluggar afhenda upprunalegu síðunni svarið og mega ekki hefja sérstaka setu.
    if (window.opener || window.self !== window.top) return false;
    try {
      if (window.sessionStorage.getItem(this.callbackRecoveryKey)) return false;
      window.sessionStorage.setItem(this.callbackRecoveryKey, '1');
    } catch {
      // Án geymslu er ekki hægt að tryggja að endurbeining endurtaki sig ekki endalaust.
      return false;
    }
    // Ógilda svarið er aldrei samþykkt; ný beiðni fær nýtt state og PKCE.
    await this.startLogin();
    return true;
  }

  completeAuthentication(): Promise<User | undefined> {
    // Callback er unnið einu sinni á hverri síðuhleðslu, líka við endurtekið mount.
    if (!this.authenticationRequest) {
      const url = this.callbackUrl ?? window.location.href;
      this.callbackUrl = undefined;
      // Geymum svarið aðeins meðan það er unnið; endurhleðsla má ekki endurnota kóðann.
      window.history.replaceState(window.history.state, '', window.location.pathname);
      this.authenticationRequest = this.finishAuthentication(url);
    }
    return this.authenticationRequest;
  }

  private isAuthenticationCallback(): boolean {
    return window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/signin-oidc';
  }

  private async finishAuthentication(url: string): Promise<User | undefined> {
    this.authenticating = true;
    try {
      // Eldri endurnýjun verður að klárast áður en ný seta er vistuð.
      await this.renewal?.catch(() => undefined);
      this.clearRetry();
      const parameters = new URL(url).searchParams;
      const hasResponse = ['code', 'state', 'error'].some(parameter => parameters.has(parameter));
      if (!hasResponse) {
        if (window.opener) throw new Error('No state in response');
        // Bein opnun eða bókamerki á callback-síðu er ekki svar frá island.is.
        const existing = await this.getUser();
        if (existing && !existing.expired) {
          this.setSessionExpired(false);
          return existing;
        }
        await this.login();
        return undefined;
      }

      const user = await this.manager.signinCallback(url);
      if (user) {
        // Ný innskráning hefur eigin refresh-token, jafnvel þótt flipinn hafi verið afritaður.
        if (!this.tabInitialization) window.sessionStorage.removeItem(this.tabSessionKey);
        window.sessionStorage.removeItem(this.callbackRecoveryKey);
        this.setSessionExpired(false);
      }
      return user;
    } catch (error) {
      this.reportAuthenticationFailure(error, 'callback');
      const missingState = error instanceof Error &&
        ['No state in response', 'No matching state found in storage'].includes(error.message);
      if (missingState && await this.recoverMissingState()) return undefined;
      throw error;
    } finally {
      this.authenticating = false;
    }
  }

  async reauthenticate(): Promise<void> {
    const previous = this.user;
    // Innskráning í sérglugga heldur óvistuðu formi í upprunalega glugganum.
    const user = await this.manager.signinPopup();
    // IAS-sub auðkennir bæði notanda og umboð. Nafn og röð annarra claims geta breyst.
    if (previous && (user.profile.sub !== previous.profile.sub ||
      user.profile.nationalId !== previous.profile.nationalId)) {
      this.user = previous;
      await this.manager.removeUser();
      throw new Error('Skráðu þig inn með sama aðgangi og áður til að halda áfram með opnar breytingar.');
    }
    this.clearRetry();
    this.setSessionExpired(false);
  }

  async logout(): Promise<void> {
    this.signingOut = true;
    this.clearRetry();
    try {
      await this.manager.signoutRedirect();
      await this.manager.removeUser();
    } catch (error) {
      this.signingOut = false;
      throw error;
    }
  }
}

export const authService = new AuthService();
